import { Alert, Box, Checkbox, Chip, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import LibraryRuleService from '../../../shared/services/library-rule/library-rule.service';
import {
  ILibraryImportableRule,
  ILibraryRuleImportClient,
  ILibraryRuleImportResult,
} from '../../../shared/services/library-rule/library-rule.model';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { BRAND_DISPLAY_FONT } from '../../pages/signup-login/auth/auth-branding';

// Pulling rules a client already has into the library.
//
// The case this exists for is the partner who wrote custom rules directly inside a client
// account before the Library existed, and the partner who onboards a client that already has
// a sensible set. Importing copies each rule up AND links the client's row to it, so the rule
// immediately reads as "pushed to that client, up to date" and the client is left with the
// one copy they already had.
//
// Bulk and multi-client, unlike the survey import, because rules come in tens per client. The
// per-rule chips carry the preview the backend worked out: the same value held by two clients
// becomes ONE library rule that both of them link to.
export const PartnerLibraryRuleImportDialog: FunctionComponent<{
  open: boolean,
  closeHandler: () => void,
  onImported: () => void,
}> = ({ open, closeHandler, onImported }) => {
  const libraryRuleService = new LibraryRuleService()
  const userNotificationsContext = useContext(UserNotificationsContext)

  const [clients, setClients] = useState<ILibraryRuleImportClient[]>()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [result, setResult] = useState<ILibraryRuleImportResult>()

  useEffect(() => {
    if (!open) return
    setIsLoading(true)
    libraryRuleService.getImportableRules()
      .then(response => {
        setClients(response.clients)
        setIsLoading(false)
      })
      .catch(err => {
        console.log(err)
        setIsLoading(false)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const close = () => {
    setSelectedIds(new Set())
    setResult(undefined)
    closeHandler()
  }

  // A rule already managed from a library has nothing to import, so it is shown greyed and
  // cannot be ticked. Everything else is fair game, including the ones that will link rather
  // than create.
  const isSelectable = (rule: ILibraryImportableRule) => rule.action !== 'skip'

  // Only clients with something to show, so an estate of twenty clients does not open as a
  // wall of empty headings.
  const importable = useMemo(
    () => (clients || []).filter(client => client.rules.length > 0),
    [clients],
  )
  const anySelectable = importable.some(client => client.rules.some(isSelectable))

  const toggleRule = (ruleId: string) => {
    setSelectedIds(previous => {
      const next = new Set(previous)
      if (next.has(ruleId)) { next.delete(ruleId) } else { next.add(ruleId) }
      return next
    })
  }

  const toggleClient = (client: ILibraryRuleImportClient) => {
    const selectable = client.rules.filter(isSelectable)
    const allSelected = selectable.every(rule => selectedIds.has(rule.id))
    setSelectedIds(previous => {
      const next = new Set(previous)
      for (const rule of selectable) {
        if (allSelected) { next.delete(rule.id) } else { next.add(rule.id) }
      }
      return next
    })
  }

  const clientSelectionState = (client: ILibraryRuleImportClient) => {
    const selectable = client.rules.filter(isSelectable)
    const chosen = selectable.filter(rule => selectedIds.has(rule.id)).length
    return {
      hasAny: selectable.length > 0,
      checked: selectable.length > 0 && chosen === selectable.length,
      indeterminate: chosen > 0 && chosen < selectable.length,
    }
  }

  const submitHandler = () => {
    if (result) { close(); onImported(); return }
    if (isSubmitting || selectedIds.size === 0) return

    const selections = importable
      .map(client => ({
        clientOrganisationId: client.id,
        ruleIds: client.rules.filter(rule => selectedIds.has(rule.id)).map(rule => rule.id),
      }))
      .filter(selection => selection.ruleIds.length > 0)

    setIsSubmitting(true)
    libraryRuleService.importFromClients(selections)
      .then(response => {
        setResult(response)
        setIsSubmitting(false)
      })
      .catch(err => {
        setIsSubmitting(false)
        userNotificationsContext.openSnackbar({
          open: true,
          message: err?.response?.data?.message || 'There was an error, please try again',
          backgroundColor: 'text.primary',
        })
        console.log(err)
      })
  }

  const ruleChips = (rule: ILibraryImportableRule) => {
    if (rule.action === 'skip') {
      return <Chip
        size='small'
        label={rule.reason === 'foreign-library' ? 'Managed by another library' : 'Already in your library'} />
    }
    if (rule.action === 'link') {
      return <>
        <Chip size='small' variant='outlined' label={`Links to "${rule.existingLibraryRuleName}"`} />
        {rule.differsFromLibrary &&
          <Chip size='small' variant='outlined' color='info' label='Differs from yours' />}
      </>
    }
    return null
  }

  return (
    <DialogWrapper
      open={open}
      onClose={close}
      title={result ? 'Import complete' : 'Import rules from a client'}
      maxWidth='sm'
      cancelHandler={close}
      submitHandler={submitHandler}
      isSubmitting={isSubmitting}
      hideCancelButton={Boolean(result)}
      disableSaveButton={!result && selectedIds.size === 0}
      submitButtonText={result ? 'Done' : `Import ${selectedIds.size} rule${selectedIds.size === 1 ? '' : 's'}`}
      submitButtonLoadingText='Importing'
    >
      {isLoading && <Stack spacing={1}>
        {[0, 1, 2].map(row => <Skeleton key={row} variant='rectangular' height={32} />)}
      </Stack>}

      {!isLoading && !result && <>
        <Typography variant='body2' sx={{ mb: 2 }}>
          Copies rules your clients already have into your library and puts you in charge of them.
          Their copies stay exactly where they are and stay linked, so they show as already up to
          date, and they can still edit them until you lock them.
        </Typography>

        {importable.length === 0 &&
          <Typography variant='body2'>None of your clients have any rules to import yet.</Typography>}

        {importable.length > 0 && !anySelectable &&
          <Alert severity='info' sx={{ mb: 2 }}>
            Every rule your clients have already came from a library, so there is nothing left to
            import.
          </Alert>}

        <Stack spacing={2}>
          {importable.map(client => {
            const state = clientSelectionState(client)
            return (
              <Box key={client.id}>
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Checkbox
                    size='small'
                    sx={{ p: 0.5 }}
                    checked={state.checked}
                    indeterminate={state.indeterminate}
                    disabled={!state.hasAny}
                    onChange={() => toggleClient(client)} />
                  <Typography variant='body2' sx={{ fontFamily: BRAND_DISPLAY_FONT }}>
                    {client.name}
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 1 }} />
                <Stack spacing={0.5}>
                  {client.rules.map(rule => {
                    const selectable = isSelectable(rule)
                    return (
                      <Stack
                        key={rule.id}
                        direction='row'
                        alignItems='center'
                        spacing={1}
                        onClick={() => { if (selectable) toggleRule(rule.id) }}
                        sx={{
                          py: 0.5, px: 1, borderRadius: 1,
                          cursor: selectable ? 'pointer' : 'default',
                          opacity: selectable ? 1 : 0.5,
                          bgcolor: selectedIds.has(rule.id) ? 'action.selected' : 'transparent',
                          '&:hover': { bgcolor: selectable ? 'action.hover' : 'transparent' },
                        }}
                      >
                        <Checkbox
                          size='small'
                          sx={{ p: 0.5 }}
                          checked={selectedIds.has(rule.id)}
                          disabled={!selectable} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant='body2'>{rule.name}</Typography>
                          {rule.conversionSummary &&
                            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                              {rule.conversionSummary}
                            </Typography>}
                        </Box>
                        {ruleChips(rule)}
                      </Stack>
                    )
                  })}
                </Stack>
              </Box>
            )
          })}
        </Stack>
      </>}

      {result && <Stack spacing={1}>
        <Alert severity={result.success ? 'success' : 'warning'}>{result.message}.</Alert>
        {result.summary.linked > 0 && <Typography variant='body2'>
          {result.summary.linked} of them matched {result.summary.linked === 1 ? 'a rule' : 'rules'} you
          already had, so {result.summary.linked === 1 ? 'that client was' : 'those clients were'} linked
          to your existing {result.summary.linked === 1 ? 'rule' : 'rules'} rather than a second copy
          being made.
        </Typography>}
        <Typography variant='body2'>
          The clients' copies are now linked to your library and show as up to date. Changes you make
          here reach them the next time you push.
        </Typography>
        {result.results.some(row => row.editedByClient) && <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          Some copies say something different from your library rule, so they are marked as edited by
          the client. Pushing skips those until you tick to overwrite them.
        </Typography>}
      </Stack>}
    </DialogWrapper>
  )
}
